import { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';

// Cache for favicon URL to avoid repeated API calls
let faviconCache = null;
let faviconPromise = null;

export const useFavicon = () => {
  const [faviconUrl, setFaviconUrl] = useState(faviconCache);

  useEffect(() => {
    const loadFavicon = async () => {
      // Return cached value if available
      if (faviconCache !== null) {
        setFaviconUrl(faviconCache);
        return;
      }

      // If a request is already in progress, wait for it
      if (faviconPromise) {
        const url = await faviconPromise;
        setFaviconUrl(url);
        return;
      }

      // Make the API call
      faviconPromise = api.get('/settings/public')
        .then(res => {
          const url = res.data?.favicon_url || '';
          faviconCache = url;
          return url;
        })
        .catch(() => '')
        .finally(() => {
          faviconPromise = null;
        });

      const url = await faviconPromise;
      setFaviconUrl(url);
    };

    loadFavicon();
  }, []); // Empty deps is intentional - only run on mount

  // Memoize the update function
  const updateFaviconLink = useCallback((rel, href) => {
    let link = document.querySelector(`link[rel="${rel}"]`);
    if (link) {
      link.href = href;
    } else {
      link = document.createElement('link');
      link.rel = rel;
      link.href = href;
      document.head.appendChild(link);
    }
  }, []);

  // Apply favicon to document when URL changes
  useEffect(() => {
    // Only update if we have a custom favicon URL
    if (!faviconUrl) return;

    // Update all favicon variants
    updateFaviconLink('icon', faviconUrl);
    updateFaviconLink('shortcut icon', faviconUrl);
    updateFaviconLink('apple-touch-icon', faviconUrl);

  }, [faviconUrl, updateFaviconLink]);

  return faviconUrl;
};

// Function to clear cache (useful after settings update)
export const clearFaviconCache = () => {
  faviconCache = null;
  faviconPromise = null;
};
